import { memo, useCallback } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { MenuItem, ListItemIcon, Skeleton, Typography, Divider } from '@mui/material'
import { DrmProvider, DrmProviderInsert } from '@nativewaves/nw-cloud-sdk/content'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { RowActionsProps } from '@shared/components/Table'
import { useDrmProviderCreateMutation, useDrmProviderDeleteMutation } from 'hooks/mutations/content/DrmProvider'

const RowActionsRaw = ({ row, onClose }: RowActionsProps<DrmProvider>) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: createDrmProvider, ...drmProviderCreateMutation } = useDrmProviderCreateMutation()
  const { mutate: deleteDrmProvider } = useDrmProviderDeleteMutation()

  const handleDuplicateDrmProvider = useCallback(() => {
    createDrmProvider(
      {
        drmProviderInsert: row.original as DrmProviderInsert,
      },
      {
        onSuccess: () => {
          onClose()
        },
      },
    )
  }, [createDrmProvider, onClose, row.original])

  const handleDeleteDrmProvider = useCallback(() => {
    onClose()

    deleteDrmProvider(
      { drmProviderId: row.original.id },
      {
        onSuccess: () =>
          enqueueSnackbar(t('domain:Workspace.ExpManifest.nameDelete', { name: row.original.name }), {
            variant: 'success',
          }),
      },
    )
  }, [deleteDrmProvider, enqueueSnackbar, onClose, row.original.id, row.original.name, t])

  return (
    <>
      <MenuItem onClick={handleDuplicateDrmProvider} disabled={drmProviderCreateMutation.isPending}>
        <ListItemIcon>
          <ControlPointDuplicate />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {drmProviderCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
        </Typography>
      </MenuItem>
      <MenuItemCopyId onSuccess={onClose} id={row.original.id} />
      <Divider sx={{ my: 1 }} component="li" />
      <MenuItemDelete onConfirm={handleDeleteDrmProvider} />
    </>
  )
}

export const RowActions = memo(RowActionsRaw)
